import { Reference } from '@shared/layer-property/layer-property.component'

/** Contains one to many `CreditCalculationStructure` structures */
export interface CreditPortfolio {
  _id?: string
  name: string
  metadata: Record<string, string>
  asset_class: string
  data_type: string
  exposure_base: string
  total_exposure: number
  expense_ratio: number
  portfolio_data: string
  portfolio_as_of_date: string // YYYY-MM-DD
  start_business_covered_date?: string
  end_business_covered_date?: string
  gross_loss_scenarios: CreditGrossLossScenario[]
  actuals: CreditPortfolioActual[]
  modified_date: string
  portfolio_results: Record<string, number> | null
  id?: number // needed for use with app-sort-table
}

export interface CreditPortfolioActual {
  actual_gls_data: string
  actual_loan_data: string
  as_of_date: string
}

export interface CreditGrossLossScenario {
  _id?: string
  name: string
  metadata: Record<string, string> // imported_from_model_runner added during import
  scenario: string
  model: string
  gross_loss_scenario_data: string
  scenario_mapping_id?: number
  loan_gross_loss_scenario_data: string
  pattach_data: string
  is_default: boolean
  scenario_type: ScenarioType
  stochastic_data?: StochasticData[]
  actuals: CreditGrossLossScenarioActual[]
  modified_date: string
  isActuals?: boolean
}

export type CreditGrossLossScenarioWithLabel = CreditGrossLossScenario & {
  label: string
}

type GrossLossScenarioId = Pick<CreditGrossLossScenario, '_id'>

export type CreditGrossLossScenarioIncluded = CreditGrossLossScenario & {
  includeOnSubmission: boolean
}

export interface CreditGrossLossScenarioActual {
  actual_gls_data: string
  as_of_date: string
}

export interface StochasticData {
  return_period: string
  gross_loss_scenario_data: string
  scenario_mapping_id?: number
}

export interface CreditModelScenarioMapping {
  id: number
  model_name: string
  model_order: number
  scenario_name: string
  scenario_order: number
  return_period?: number // applicable for stochastic
}

export type CreditModelScenarioGlsMapping = CreditModelScenarioMapping &
  GrossLossScenarioId &
  GrossLossScenarioCategoryOption

export type GrossLossScenarioCategoryOption = {
  key: string // category-id
  category: ScenarioCategory
  text: string
}

export enum ScenarioType {
  Stochastic = 'Stochastic',
  Deterministic = 'Deterministic',
}

export enum ScenarioCategory {
  Origination = 'Origination',
  Actual = 'Actual',
}

export const SCENARIO_TYPE_REFERENCES: Reference[] = [
  {
    value: ScenarioType.Deterministic,
    viewValue: ScenarioType.Deterministic,
  },
  {
    value: ScenarioType.Stochastic,
    viewValue: ScenarioType.Stochastic,
  },
]
export interface StochasticData {
  return_period: string
  gross_loss_scenario_data: string
  scenario_mapping_id?: number
}

export interface PortfolioStats {
  statName: string
  statValue: number
}

export const PortfolioStatNameMapping = {
  avgCreditScore: 'Average Credit Score',
  totalOriginalUPB: 'Total Original UPB',
  avgDTI: 'Average DTI',
  avgCLTV: 'Average CLTV',
  avgLTV: 'Average LTV',
  avgOriginalInterestRate: 'Average Original Interest Rate',
}

export interface CreditModelScenarioMapping {
  id: number
  model_name: string
  scenario_name: string
  return_period?: number // applicable for stochastic
  scenario_storage_location: string
}

export const MILLIMAN_2022_MODEL = 'Milliman 2022.1'
export const MILLIMAN_2024_MODEL = 'Milliman 2024'
export const ADCO_MODEL = 'ADCO'
export const LEAPS_MODEL = 'LEAPs'
export const STOCHASTIC_SCENARIO = 'Stochastic'
export const BASELINE_SCENARIO = 'Baseline'

export const GROSS_LOSS_SCENARIO_MODEL_RUNNER_METADATA_KEYS = {
  RUN_ID: 'run_id',
  IMPORT_DATE: 'import_date',
} as const

export interface BlobTransferResult {
  sourceHref: string
  destinationHref: string
}
